import React, { useEffect, useState, useRef, useMemo } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { MapContainer, TileLayer, Polyline, Marker } from "react-leaflet";
import { renderToString } from "react-dom/server";
import { RiPlaneFill, RiFlightTakeoffFill, RiFlightLandFill } from "@remixicon/react";
import { useSearchParams } from "react-router-dom";
import { apiClient } from "@/lib/api";

// FR24 direct client (uncomment if switching back from backend proxy)
// import Client from "@/features/flightradar/client";
// const client = new Client({ apiToken: process.env.REACT_APP_FR24_API_TOKEN, apiVersion: "v1" });

const CARTO_TILES = "https://basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png";
const CARTO_ATTRIBUTION = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/">CARTO</a>';

const INITIAL_CENTER = [20, 0]; // [lat, lng]
const INITIAL_ZOOM = 2;

// Adaptive polling intervals based on ETA remaining (minutes)
// > 30 min (cruising)   → 5 min
// 15–30 min (descending) → 2 min
// 5–15 min (approaching) → 30 s
// < 5 min (final approach) → 15 s
function getPollingInterval(etaMinutes) {
  if (etaMinutes === null || etaMinutes === undefined || etaMinutes > 30) return 5 * 60 * 1000;
  if (etaMinutes > 15) return 2 * 60 * 1000;
  if (etaMinutes > 5) return 30 * 1000;
  return 15 * 1000;
}

// Haversine distance in km
function haversine(lat1, lon1, lat2, lon2) {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

// Dead reckoning: project position forward by `seconds` at given speed/heading
// Uses great-circle (rhumb-line approximation is sufficient for short intervals)
function projectPosition(lat, lng, speedKmh, headingDeg, seconds) {
  if (!speedKmh || speedKmh <= 0 || !seconds) return { lat, lng };
  const R = 6371;
  const d = (speedKmh * seconds) / 3600 / R; // angular distance in radians
  const bearing = (headingDeg * Math.PI) / 180;
  const lat1 = (lat * Math.PI) / 180;
  const lon1 = (lng * Math.PI) / 180;
  const lat2 = Math.asin(
    Math.sin(lat1) * Math.cos(d) + Math.cos(lat1) * Math.sin(d) * Math.cos(bearing)
  );
  const lon2 =
    lon1 +
    Math.atan2(
      Math.sin(bearing) * Math.sin(d) * Math.cos(lat1),
      Math.cos(d) - Math.sin(lat1) * Math.sin(lat2)
    );
  return {
    lat: (lat2 * 180) / Math.PI,
    lng: (((lon2 * 180) / Math.PI) + 540) % 360 - 180, // normalise to [-180, 180]
  };
}

function FlightTracker({ dbFlight, tailNumber, onFlightData }) {
  const [searchParams] = useSearchParams();
  const trackingToken = searchParams.get("token");
  const [position, setPosition] = useState(null);
  const [heading, setHeading] = useState(0);
  const [routeData, setRouteData] = useState({ trackCoords: [], origCoord: null, destCoord: null });
  const mapRef = useRef(null);
  const lastPosRef = useRef(null);
  const trackFetchedRef = useRef(false);
  const flyingRef = useRef(false);
  const routeDataRef = useRef({ origCoord: null, destCoord: null });

  // Adaptive polling
  const pollTimeoutRef = useRef(null);

  // Animation refs (shared between the 2s ticker and the fetch logic)
  const deadReckonRef = useRef(null);   // { lat, lng, speedKmh, headingDeg, lastTime }
  const correctionRef = useRef(null);   // { fromLat, fromLng, toLat, toLng, startTime } | null
  const displayPosRef = useRef(null);   // last position set on the map

  // ─── 2-second animation ticker ────────────────────────────────────────────
  // Between API polls the pin moves every 2 s via dead reckoning.
  // When a correction arrives it glides smoothly over 6 s instead of jumping.
  // The pin freezes within 2 km of the destination to prevent overshoot.
  useEffect(() => {
    const CORRECTION_GLIDE_MS = 6000;
    const OVERSHOOT_FREEZE_KM = 2;

    const tick = () => {
      const now = Date.now();
      let newPos = null;

      if (correctionRef.current) {
        // Smooth glide to corrected API position
        const { fromLat, fromLng, toLat, toLng, startTime } = correctionRef.current;
        const elapsed = now - startTime;

        if (elapsed >= CORRECTION_GLIDE_MS) {
          // Glide complete — resume dead reckoning from corrected position
          newPos = { lat: toLat, lng: toLng };
          correctionRef.current = null;
          if (deadReckonRef.current) {
            deadReckonRef.current.lat = toLat;
            deadReckonRef.current.lng = toLng;
            deadReckonRef.current.lastTime = now;
          }
        } else {
          const t = elapsed / CORRECTION_GLIDE_MS;
          newPos = {
            lat: fromLat + (toLat - fromLat) * t,
            lng: fromLng + (toLng - fromLng) * t,
          };
        }
      } else if (deadReckonRef.current) {
        // Dead reckoning
        const dr = deadReckonRef.current;
        const dt = (now - dr.lastTime) / 1000; // seconds since last tick
        const dest = routeDataRef.current.destCoord;

        // Overshoot prevention: freeze pin once it's within 2 km of destination
        if (dest) {
          const distToDest = haversine(dr.lat, dr.lng, dest[0], dest[1]);
          if (distToDest <= OVERSHOOT_FREEZE_KM) {
            newPos = { lat: dr.lat, lng: dr.lng };
          } else {
            newPos = projectPosition(dr.lat, dr.lng, dr.speedKmh, dr.headingDeg, dt);
            dr.lat = newPos.lat;
            dr.lng = newPos.lng;
            dr.lastTime = now;
          }
        } else {
          newPos = projectPosition(dr.lat, dr.lng, dr.speedKmh, dr.headingDeg, dt);
          dr.lat = newPos.lat;
          dr.lng = newPos.lng;
          dr.lastTime = now;
        }
      }

      if (newPos) {
        displayPosRef.current = newPos;
        setPosition(newPos);
      }
    };

    const animInterval = setInterval(tick, 2000);
    return () => clearInterval(animInterval);
  }, []); // runs once — uses only refs, no stale closures

  // ─── Adaptive polling ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!tailNumber) return;

    setPosition(null);
    setRouteData({ trackCoords: [], origCoord: null, destCoord: null });
    setHeading(0);
    trackFetchedRef.current = false;
    lastPosRef.current = null;
    deadReckonRef.current = null;
    correctionRef.current = null;
    displayPosRef.current = null;

    let mounted = true;

    function scheduleNext(etaMinutes) {
      if (!mounted) return;
      clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = setTimeout(fetchFlight, getPollingInterval(etaMinutes));
    }

    async function fetchFlight() {
      if (!mounted) return;
      try {
        // --- Backend proxy version ---
        const response = await apiClient.getLiveFlightPositions(tailNumber, trackingToken ? { tracking_token: trackingToken } : {});
        const flights = response?.data || [];
        console.log("flights", flights);
        console.log("dbFlight", dbFlight);

        if (!mounted) return;

        if (!flights.length) {
          scheduleNext(null);
          return;
        }

        const flight = flights[0];
        const newPos = { lng: flight.lon, lat: flight.lat };
        const newHeading = flight.track || 0;
        // FR24 gspeed is in knots → convert to km/h for dead reckoning
        const speedKmh = (flight.gspeed || 0) * 1.852;

        const last = lastPosRef.current;
        const moved = !last || last.lng !== newPos.lng || last.lat !== newPos.lat;
        const rotated = !last || last.heading !== newHeading;

        if (moved) {
          // First fix: fly the map to the position and set it directly
          if (!last && mapRef.current) {
            flyingRef.current = true;
            mapRef.current.once("moveend", () => { flyingRef.current = false; });
            mapRef.current.flyTo([newPos.lat, newPos.lng], 6, { duration: 1 });
            displayPosRef.current = newPos;
            setPosition(newPos);
          } else if (displayPosRef.current) {
            // Subsequent fix: start a smooth 6-second glide to the corrected position
            correctionRef.current = {
              fromLat: displayPosRef.current.lat,
              fromLng: displayPosRef.current.lng,
              toLat: newPos.lat,
              toLng: newPos.lng,
              startTime: Date.now(),
            };
          }

          // (Re-)initialise dead reckoning from the authoritative API position
          deadReckonRef.current = {
            lat: newPos.lat,
            lng: newPos.lng,
            speedKmh,
            headingDeg: newHeading,
            lastTime: Date.now(),
          };
        } else if (deadReckonRef.current) {
          // Position unchanged — update speed/heading so dead reckoning stays accurate
          deadReckonRef.current.speedKmh = speedKmh;
          deadReckonRef.current.headingDeg = newHeading;
        }

        // --- FR24 (flight tracks + airport coords) — fetched once ---
        if (!trackFetchedRef.current && flight.fr24_id) {
          trackFetchedRef.current = true;
          const origCode = flight.orig_icao || flight.orig_iata;
          const destCode = flight.dest_icao || flight.dest_iata;

          // --- Backend proxy version ---
          const tokenParams = trackingToken ? { tracking_token: trackingToken } : {};
          const [tracksResult, origResult, destResult] = await Promise.allSettled([
            apiClient.getFlightTracks(flight.fr24_id, tokenParams),
            origCode ? apiClient.getAirportFull(origCode, tokenParams) : Promise.reject(),
            destCode ? apiClient.getAirportFull(destCode, tokenParams) : Promise.reject(),
          ]);

          if (!mounted) return;

          const tracksData = tracksResult.status === "fulfilled" ? tracksResult.value : null;
          const trackPoints = Array.isArray(tracksData)
            ? tracksData[0]?.tracks || []
            : tracksData?.data?.[0]?.tracks || [];
          const origCoord = origResult.status === "fulfilled" ? [origResult.value.lat, origResult.value.lon] : null;
          const destCoord = destResult.status === "fulfilled" ? [destResult.value.lat, destResult.value.lon] : null;

          routeDataRef.current = { origCoord, destCoord };
          setRouteData({
            trackCoords: trackPoints.map((pt) => [pt.lat, pt.lon]),
            origCoord,
            destCoord,
          });
        }

        if (rotated) setHeading(newHeading);

        const isFirst = !last;
        lastPosRef.current = { lng: newPos.lng, lat: newPos.lat, heading: newHeading };

        // Compute progress and distances from coordinates (use ref to avoid stale closure)
        const orig = routeDataRef.current.origCoord;
        const dest = routeDataRef.current.destCoord;
        let progress = 0, distFlown = null, distRemaining = null;
        if (orig && dest) {
          const totalDist = haversine(orig[0], orig[1], dest[0], dest[1]);
          distFlown = haversine(orig[0], orig[1], newPos.lat, newPos.lng);
          distRemaining = Math.max(0, totalDist - distFlown);
          progress = totalDist > 0 ? Math.min((distFlown / totalDist) * 100, 100) : 0;
        }

        if (onFlightData && (isFirst || moved || rotated)) {
          onFlightData({ ...flight, progress, distFlown, distRemaining });
        }

        // ── Adaptive interval: derive ETA remaining in minutes ──
        let etaMinutes = null;
        if (flight.eta) {
          // eta may be Unix seconds (< 1e12) or ms / ISO string
          const etaMs =
            typeof flight.eta === "number" && flight.eta < 1e12
              ? flight.eta * 1000
              : new Date(flight.eta).getTime();
          const nowMs =
            flight.timestamp
              ? typeof flight.timestamp === "number" && flight.timestamp < 1e12
                ? flight.timestamp * 1000
                : new Date(flight.timestamp).getTime()
              : Date.now();
          etaMinutes = Math.max(0, (etaMs - nowMs) / 60000);
        }

        scheduleNext(etaMinutes);
      } catch (err) {
        console.error(err);
        scheduleNext(null); // fallback to default 5-minute interval
      }
    }

    fetchFlight();

    return () => {
      mounted = false;
      clearTimeout(pollTimeoutRef.current);
    };
  }, [tailNumber, dbFlight]);

  // Leaflet marker icons
  const departureIcon = useMemo(() => L.divIcon({
    html: renderToString(<RiFlightTakeoffFill size={24} color="#0a3560" />),
    className: "",
    iconSize: [24, 24],
    iconAnchor: [12, 24],
  }), []);

  const arrivalIcon = useMemo(() => L.divIcon({
    html: renderToString(<RiFlightLandFill size={24} color="#8797a7" />),
    className: "",
    iconSize: [24, 24],
    iconAnchor: [12, 24],
  }), []);

  const planeIcon = useMemo(() => L.divIcon({
    html: renderToString(
      <div style={{ transform: `rotate(${heading}deg)` }}>
        <RiPlaneFill size={34} color="#006fdd" />
      </div>
    ),
    className: "",
    iconSize: [34, 34],
    iconAnchor: [17, 17],
  }), [heading]);

  return (
    <div style={{ position: "relative", width: "100%", height: "100%" }}>
      <MapContainer
        ref={mapRef}
        center={INITIAL_CENTER}
        zoom={INITIAL_ZOOM}
        style={{ width: "100%", height: "100%" }}
        attributionControl={false}
        zoomControl={false}
        maxZoom={18}
        minZoom={2}
        worldCopyJump={false}
      >
        <TileLayer
          url={CARTO_TILES}
          attribution={CARTO_ATTRIBUTION}
        />

        {/* Flight track (solid line) */}
        {routeData.trackCoords.length >= 2 && (
          <Polyline
            positions={routeData.trackCoords}
            pathOptions={{ color: "#0a3560", weight: 2.5 }}
          />
        )}

        {/* Remaining route (dashed line) */}
        {position && routeData.destCoord && (
          <Polyline
            positions={[[position.lat, position.lng], routeData.destCoord]}
            pathOptions={{ color: "#0a3560", weight: 2, dashArray: "8 8", opacity: 0.4 }}
          />
        )}

        {/* Departure pin */}
        {routeData.origCoord && (
          <Marker position={routeData.origCoord} icon={departureIcon} />
        )}

        {/* Arrival pin */}
        {routeData.destCoord && (
          <Marker position={routeData.destCoord} icon={arrivalIcon} />
        )}

        {/* Current position */}
        {position && (
          <Marker position={[position.lat, position.lng]} icon={planeIcon} />
        )}
      </MapContainer>
    </div>
  );
}

export default React.memo(FlightTracker);
